import { RouteRecordRaw } from 'vue-router';
import { getStaticRedirects } from '@/utils/redirects';
import { buildTutorialLanguageRoute, buildUseCaseLanguageRoute } from '@/utils/dynamicRoute';
import { OpenGraphPageType } from '@/utils/openGraph';
import { isVonageBrand } from '@/constants';

const routes: RouteRecordRaw[] = [
  // redirects from content/config/redirects.yml
  ...getStaticRedirects(),
  // built useCase route from redirect.yml to support legacy language routes
  buildUseCaseLanguageRoute(),
  buildTutorialLanguageRoute(),
  {
    path: '/:product/:subproduct*/building-blocks/:page(.*)*',
    redirect: to => to.path.replace('building-blocks', 'code-snippets'),
  },
  { path: '/', redirect: '/home' },
  {
    path: '/documentation',
    component: () => import('./pages/DocumentationPage.vue'),
    meta: { title: 'title.documentation' },
  },
  {
    path: '/use-cases',
    component: () => import('./pages/UseCasesPage.vue'),
    props: () => ({ type: 'use-cases' }),
    meta: { title: 'title.useCases' },
  },
  {
    path: '/use-cases/lang/:language',
    component: () => import('./pages/UseCasesPage.vue'),
    props: ({ params: { language } }) => ({ type: 'use-cases', language }),
    meta: { title: 'title.useCases' },
  },
  {
    path: '/use-cases/:id',
    component: () => import('./pages/DocumentPage.vue'),
    props: ({ params: { id } }) => ({ type: 'use-cases', id }),
    meta: { ogType: OpenGraphPageType.Document, type: 'use-cases' },
  },
  {
    path: '/integration',
    component: () => import('./pages/IntegrationsPage.vue'),
    meta: { title: 'title.integration' },
  },
  {
    path: '/extend/:id',
    redirect: '/integration/:id',
  },
  {
    path: '/integration/:id',
    component: () => import('./pages/IntegrationPage.vue'),
    props: ({ params: { id } }) => ({ type: 'integration', id }),
    meta: { ogType: OpenGraphPageType.Document, type: 'integration' },
  },
  {
    path: '/api',
    component: () => import('./pages/Api.vue'),
  },
  {
    path: '/api/:id(.*)',
    component: () => import('./pages/ApiDocumentPage.vue'),
    props: ({ params: { id } }) => ({ id }),
    meta: { title: 'title.apiReference', ogType: OpenGraphPageType.ApiSpec },
  },
  {
    path: '/api-errors',
    component: () => import('./pages/ApiErrorsPage.vue'),
    props: () => ({ type: 'generic' }),
    meta: { title: 'title.apiErrors' },
  },
  // Handle legacy links containing a version number
  {
    path: '/api-errors/:type.:version',
    redirect: ({ params: { type } }) => `/api-errors/${type}`,
  },
  {
    path: '/api-errors/:type',
    component: () => import('./pages/ApiErrorsPage.vue'),
    props: ({ params: { type } }) => ({ type }),
    meta: { title: 'title.apiReference' },
  },
  {
    path: '/changelogs',
    component: () => import('./pages/Changelogs.vue'),
    meta: { title: 'title.changelogs' },
  },
  {
    path: '/changelogs/:id(.*)',
    component: () => import('./pages/DocumentPage.vue'),
    props: ({ params: { id } }) => ({ type: 'changelogs', id, hideNavigationSidebar: true }),
    meta: { ogType: OpenGraphPageType.Document, type: 'changelogs' },
  },
  {
    path: '/sdk/stitch/:language',
    component: () => import('./pages/SDKDocs.vue'),
    props: ({ params }) => ({ ...params, path: 'index' }),
  },
  {
    path: '/sdk/stitch/:language/:path+',
    component: () => import('./pages/SDKDocs.vue'),
    props: ({ params }) => ({ ...params }),
  },
  {
    path: '/:product/:subproduct*/tutorials/:id/:chapter?/:codeLang?',
    redirect: ({ params: { id, chapter, codeLang } }) => `/tutorials/${id}/${chapter}/${codeLang}`,
  },
  {
    path: '/tutorials/:id',
    component: () => import('./pages/TutorialPage.vue'),
    props: ({ params: { id } }) => ({ id }),
    meta: { analytics: { trackPageView: false } },
  },
  {
    path: '/tutorials/:id/:chapter?/:codeLang?',
    component: () => import('./pages/TutorialPage.vue'),
    props: ({ params: { id, chapter, codeLang } }) => ({ id, chapter, codeLang }),
    meta: { ogType: OpenGraphPageType.Tutorial },
  },
  {
    path: '/tutorials',
    component: () => import('./pages/TutorialsPage.vue'),
    meta: { title: 'title.tutorials' },
  },
  {
    path: '/auth/camara',
    component: () => import('./pages/DocumentPage.vue'),
    props: () => ({
      type: 'documentation',
      id: `auth/camara.${window.branding.vendorId}`,
      hideNavigationSidebar: true,
    }),
    // ignore this page as it's served by the dashboard as MFE
    meta: { ogType: OpenGraphPageType.NoOpenGraphData },
  },
  // catch-all for documentation as it does not prepend /documentation but the app name
  {
    path: '/:app/:id(.*)',
    component: () => import('./pages/DocumentPage.vue'),
    props: ({ params: { id, app } }) => ({ type: 'documentation', id: `/${app}/${id}` }),
    meta: { ogType: OpenGraphPageType.Document, type: 'documentation' },
  },
  {
    path: '/tools',
    component: () => import('./pages/ToolsPage.vue'),
    meta: { title: 'title.tools' },
  },
  {
    path: '/jwt',
    component: () => import('./pages/JWTPage.vue'),
    meta: { title: 'title.jwtGenerator' },
  },
  // APIDP-744 remove the locale from the sign-up link
  {
    path: '/sign-up',
    component: () => undefined,
    beforeEnter: ({ fullPath }) => {
      window.location.href = fullPath;
    },
  },
  {
    path: '/:catchAll(.*)',
    component: () => import('./pages/system/NotFoundErrorPage.vue'),
    meta: { title: 'title.notFound' },
  },
  /*
  To be replaced with proper SSR mechanism to handle hidden pages.
   */
  ...(isVonageBrand
    ? [
        {
          path: '/careers',
          component: () => import('./pages/CareersPage.vue'),
          meta: { title: 'title.careers' },
        },
        {
          path: '/community/slack',
          component: () => import('./pages/community/Slack.vue'),
          meta: { title: 'title.community' },
        },
        {
          path: '/admin/events/registration/:eventId',
          component: () => import('./pages/admin/EventRegistrationAdmin.vue'),
          props: ({ params: { eventId } }: { params: { eventId: string } }) => ({ eventId }),
          meta: { title: 'Event Registration Admin Page' },
        },
      ]
    : []),
];

export default routes;
